import type { LabelStack } from '@atlaskit/react-ufo/interaction-context';
import type { CustomTiming, InteractionType, Mark } from '@atlaskit/react-ufo/common';

import type { LayoutShiftAttributionPayload } from '@confluence/browser-metrics/entry-points/getLayoutShiftWithAttribution.ts';
import { getWaterfallTimings } from '@confluence/browser-metrics/entry-points/collectInitialLoadWaterfallTimings.ts';
import {
	pageLoadEventPlugin,
	customEventPlugin,
	ssrRegionPlugin,
	frontendNetworkPlugin,
} from '@confluence/browser-metrics/entry-points/plugins.ts';
import { getBuildInfo } from '@confluence/build-info';
import { fg } from '@confluence/feature-gating';
import { getExtensionDimensions } from '@confluence/browser-metrics/entry-points/ExtensionDimensions.ts';

import { readPerfProbe } from './collectPerfProbe.ts';
import { getSSRDoneTime } from './ssr.ts';
import { getStickyVersionsState } from './stickyVersionsState.ts';

declare global {
	interface Window {
		__PO_ASSET_DISPLAYED__?: boolean;
	}
}

export const additionalPayloadData = (
	interaction: {
		type: InteractionType;
		id: string;
		end: number;
		start: number;
		ufoName: string;
		marks: Mark[];
		customTimings: { labelStack: LabelStack; data: CustomTiming }[];
	},
	otherMetadataFromSessionData: {
		cloudId: string;
		isShardPreProd: boolean;
		layoutShiftWithAttribution: LayoutShiftAttributionPayload;
	},
): {
	confluence: any;
} => {
	let fmpPatch = {};
	if (interaction.type === 'page_load') {
		Object.entries(getWaterfallTimings()).forEach(([key, value]) => {
			interaction.customTimings.push({ labelStack: [], data: { [key]: value } });
		});

		// In UFO realm, there's seemingly a bug causing `metric:fmp` to be populated in ~10-15%
		// of cases probably due to some race condition. Adding it here as a fallback until
		// investigation with #help-devinfra-fe-observability is completed.
		// Covered by ticket https://product-fabric.atlassian.net/browse/CCP-7117
		const fmpTime = getSSRDoneTime();
		if (fmpTime) {
			fmpPatch = {
				'metric:fmp': fmpTime,
			};
		}
	} else if (interaction.type === 'transition') {
		// For transitions, FMP is tracked via the UFOTransitionMarkFMP component which places
		// an 'fmp' custom mark on the interaction. We read the mark time and compute the
		// relative FMP as the offset from interaction start.
		const fmpMark = interaction.marks.find((mark) => mark.type === 'custom' && mark.name === 'fmp');
		if (fmpMark) {
			fmpPatch = {
				'metric:fmp': Math.round(fmpMark.time - interaction.start),
			};
		}
	}

	const buildInfoData = getBuildInfo();
	const buildInfo: any = {};

	if (buildInfoData) {
		Object.keys(buildInfoData).forEach((item) => {
			const newKey = `buildInfo:${item}`;
			buildInfo[newKey] = (buildInfoData as any)[item];
		});
	}

	const data = {
		...pageLoadEventPlugin({
			stop: interaction.end,
			start: interaction.start,
			key: interaction.ufoName,
		}),
		...customEventPlugin({
			stop: interaction.end,
			start: interaction.start,
			key: interaction.ufoName,
		}),
		...frontendNetworkPlugin({
			stop: interaction.end,
			start: interaction.start,
			key: interaction.ufoName,
		}),
		...ssrRegionPlugin(),
		...buildInfo,
		...otherMetadataFromSessionData,

		'stickyVersions:state': getStickyVersionsState(),
		postOfficeAssetsDisplayed: window.__PO_ASSET_DISPLAYED__,
		...((interaction.type === 'page_load' || interaction.type === 'transition') &&
		fg('confluence_ufo_banner_extension_dimensions')
			? getExtensionDimensions()
			: {}),

		// Client performance diagnostic covariates (cpuMs, softwareGL,
		// scriptStartAfterHtml). Initial load only — the signals explain
		// device/environment causes of a slow first load, not transitions.
		...(interaction.type === 'page_load' ? readPerfProbe() : {}),
	};

	return {
		...fmpPatch,
		confluence: data,
	};
};
